import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {
  selectPagination,
  setPagination,
  setPaginationElementsCount,
  PAGINATION,
  EMPTY_PAGINATION_OBJECT
} from 'ducks/pagination';
import {composeActions} from 'ducks/common';
import {storePaginationInUrl} from 'ducks/urls';
import {getPaginationFromUrl} from 'utils/pagination';
import {ExtraAction} from 'ducks/types';

const defaultOptions = {
  saveToUrl: false,
  pageSizes: [10, 25, 50],
  pageSize: 10
};

export interface WithPaginationProps {
  getPagination: () => {
    pageSize: number;
    pageSizes: number[];
    offset: number;
    elementsCount: number;
  };
  onPaginationChange: (
    x: {pageSize: number; offset: number},
    extraActions?: ExtraAction[]
  ) => any;
}

const withPagination = <T extends any>(
  Component: React.ComponentType<T & WithPaginationProps>,
  namespace: string,
  options: Partial<typeof defaultOptions> = {}
) => {
  const combinedOptions = {...defaultOptions, ...options};

  class WrappedComponent extends React.Component<any> {
    state = {
      pagionationHasMounted: false
    };

    componentDidMount() {
      const paginationFromUrl = getPaginationFromUrl();

      if (combinedOptions.saveToUrl && !_.isEmpty(paginationFromUrl)) {
        this.props.setPagination({
          ...paginationFromUrl,
          namespace
        });
      } else {
        // Sets initial pagination to EMPTY
        this.props.setPagination({
          ...EMPTY_PAGINATION_OBJECT,
          pageSize: combinedOptions.pageSize,
          namespace
        });
      }

      // This will be set by the initial fetch of the wrapped component
      // The paginator should handle the event of elementsCount being 0
      this.setPaginationElementsCount({elementsCount: 0});

      this.setState({pagionationHasMounted: true});
    }

    componentWillUnmount() {
      if (combinedOptions.saveToUrl) {
        this.props.storePaginationInUrl({
          paginationSelector: () => ({offset: null, pageSize: null})
        });
      }
    }

    getPagination = () => ({
      ...this.props.paginationInStore,
      pageSizes: combinedOptions.pageSizes
    });

    onPaginationChange = (
      {
        pageSize,
        offset
      }: {
        pageSize: number;
        offset: number;
      },
      extraActions = []
    ) => {
      const actions: Array<() => any> = [
        () => setPagination({pageSize, offset, namespace})
      ];

      if (combinedOptions.saveToUrl) {
        actions.push(() =>
          storePaginationInUrl({
            paginationSelector: state => selectPagination(state, namespace)
          })
        );
      }

      return this.props.composeActions([...actions, ...extraActions]);
    };

    setPaginationElementsCount = ({elementsCount}, extraActions = []) => {
      return this.props.composeActions([
        () => setPaginationElementsCount({elementsCount, namespace}),
        ...extraActions
      ]);
    };

    render() {
      // This downcast is important to remove the added props of the WrappedComponent
      const restOfProps: T = this.props;

      // This fixes a pagination bug with the HOC getting mounted after the wrapped Component.
      // The wrapped component then uses the default value for pagination from the store because the
      // requested value hasn't been set yet.
      if (!this.state.pagionationHasMounted) return null;

      return (
        <Component
          {...restOfProps}
          getPagination={this.getPagination}
          onPaginationChange={this.onPaginationChange}
        />
      );
    }
  }

  const mapStateToProps = state => ({
    paginationInStore: selectPagination(state, namespace)
  });

  const mapDispatchToProps = {
    composeActions,
    setPagination,
    storePaginationInUrl
  };

  return connect(mapStateToProps, mapDispatchToProps)(
    WrappedComponent as React.ComponentType<T>
  );
};

export {withPagination, PAGINATION};
